import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Stethoscope, Building2, Home, Tractor, Mail, MapPin,
  FileText, CheckCircle, Clock, BookOpen, Users, Target,
  TrendingUp, Award, ChevronRight, Menu, X, Shield,
  BookMarked, GraduationCap, FlaskConical, Ban, Star, Phone
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 280, damping: 32 } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const links = [
    { label: "Home", href: "#home" },
    { label: "Academic Assistance", href: "#academic" },
    { label: "Rental Property", href: "#rental" },
    { label: "Air BnB", href: "#airbnb" },
    { label: "Farming & Livestock", href: "#farming" },
    { label: "About", href: "#about" },
    { label: "Payment", href: "#payment" },
    { label: "Contact", href: "#contact" },
  ];

  const scrollTo = (href: string) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"}`}>
      <div className="container mx-auto px-4 flex items-center justify-between h-16">
        <button onClick={() => scrollTo("#home")} className="flex flex-col text-left">
          <span className="font-bold text-sm text-primary leading-tight">Hlophe Ongemhlophe Group</span>
          <span className="text-xs text-muted-foreground font-mono tracking-widest">PTY LTD</span>
        </button>
        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button key={l.label} onClick={() => scrollTo(l.href)}
              className="px-3 py-1.5 text-xs font-medium text-foreground/70 hover:text-primary rounded-md transition-colors hover:bg-muted">
              {l.label}
            </button>
          ))}
        </nav>
        <button className="lg:hidden p-2 rounded-md hover:bg-muted text-foreground" onClick={() => setOpen(!open)}>
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="lg:hidden border-t bg-white">
          {links.map((l) => (
            <button key={l.label} onClick={() => scrollTo(l.href)}
              className="w-full text-left px-6 py-3 text-sm font-medium text-foreground/80 hover:bg-muted hover:text-primary border-b border-border/50 transition-colors">
              {l.label}
            </button>
          ))}
        </motion.div>
      )}
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  const cards = [
    { icon: Stethoscope, title: "Academic Assistance Services", status: "Now Available", active: true },
    { icon: Building2, title: "Rental Property Business", status: "Coming Soon", active: false },
    { icon: Home, title: "Air BnB Business", status: "Coming Soon", active: false },
    { icon: Tractor, title: "Farming and Livestock", status: "Coming Soon", active: false },
  ];
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(135deg, #f0f4f8 0%, #e8eff7 50%, #ddeaf5 100%)" }}>
      <div className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-10"
        style={{ background: "radial-gradient(circle, #1A67B1 0%, transparent 70%)" }} />
      <div className="absolute bottom-20 left-10 w-64 h-64 rounded-full opacity-10"
        style={{ background: "radial-gradient(circle, #0A427F 0%, transparent 70%)" }} />
      <div className="container mx-auto px-4 pt-24 pb-16 text-center">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
          <motion.h1 variants={fadeUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
            <span style={{ color: "#0A427F" }}>Hlophe Ongemhlophe</span><br />
            <span style={{ color: "#1A67B1" }}>Group</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="text-xl text-muted-foreground mb-3">
            Building Excellence Across Multiple Industries
          </motion.p>
          <motion.p variants={fadeUp} className="text-sm text-muted-foreground font-mono mb-12">
            Registered Business in South Africa | CIPC: 2025/912423/07 | B-BBEE Compliant
          </motion.p>
          <motion.div variants={staggerContainer} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
            {cards.map(({ icon: Icon, title, status, active }) => (
              <motion.div key={title} variants={fadeUp}
                whileHover={{ y: -4, transition: { type: "spring", stiffness: 400, damping: 30 } }}
                className={`rounded-xl p-5 text-center cursor-pointer border transition-all ${active ? "bg-white border-accent shadow-md" : "bg-white/70 border-border/50 shadow-sm"}`}>
                <Icon size={32} className="mx-auto mb-3" style={{ color: active ? "#1A67B1" : "#94a3b8" }} />
                <p className={`text-sm font-semibold leading-tight mb-2 ${active ? "text-foreground" : "text-muted-foreground"}`}>{title}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${active ? "bg-green-100 text-green-700" : "bg-amber-50 text-amber-600"}`}>{status}</span>
              </motion.div>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => scrollTo("#academic")}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
              style={{ background: "#0A427F" }}>
              Explore Academic Services <ChevronRight size={16} />
            </button>
            <button onClick={() => scrollTo("#contact")}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-sm border-2 transition-all hover:bg-muted"
              style={{ borderColor: "#0A427F", color: "#0A427F" }}>
              Contact Us
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────
function About() {
  const values = [
    { icon: Award, title: "Academic Excellence", desc: "High-quality academic assistance across research proposals, data analysis, literature reviews, thesis support, and manuscript writing for postgraduate scholars." },
    { icon: Shield, title: "Professional Standards", desc: "Maintain the highest professional standards in all services, ensuring compliance with academic guidelines and ethical practices." },
    { icon: Clock, title: "Timely Delivery", desc: "Guarantee 72-hour turnaround time for all academic assistance services, ensuring scholars meet their deadlines." },
    { icon: Target, title: "Scholar Success", desc: "Support students and postgraduate scholars at every stage — from research proposals and data analysis through to thesis writing and academic publication." },
    { icon: CheckCircle, title: "Quality Assurance", desc: "Implement rigorous quality control processes to ensure all deliverables meet academic standards and client expectations." },
    { icon: TrendingUp, title: "Continuous Improvement", desc: "Regularly update our methodologies and knowledge base to reflect current trends in healthcare education and academic research." },
  ];
  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="text-center mb-14">
          <motion.p variants={fadeUp} className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Who We Are</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-primary mb-4">About Hlophe Ongemhlophe Group</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            A registered South African business committed to excellence across multiple sectors. We combine professional expertise with a dedication to quality service delivery.
          </motion.p>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto mt-3 leading-relaxed">
            Based in KwaZulu Natal, we are proud to be B-BBEE compliant and tax compliant, operating with full transparency and regulatory adherence.
          </motion.p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map(({ icon: Icon, title, desc }) => (
            <motion.div key={title} variants={fadeUp} className="p-6 rounded-xl border border-border bg-muted/30 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: "#e8eff7" }}>
                <Icon size={20} style={{ color: "#0A427F" }} />
              </div>
              <h3 className="font-semibold text-foreground mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Academic Assistance ──────────────────────────────────────────────────────
function AcademicAssistance() {
  const services = [
    { icon: FileText, label: "Article & Manuscript Writing for DHET-accredited Journals" },
    { icon: BookOpen, label: "Research Proposals" },
    { icon: FlaskConical, label: "Data Analysis" },
    { icon: BookMarked, label: "Academic Guidance" },
    { icon: Target, label: "Literature Review" },
    { icon: GraduationCap, label: "Thesis Support" },
    { icon: Clock, label: "72-hour Turnaround" },
    { icon: Award, label: "Harvard Referencing" },
  ];
  const scrollToContact = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section id="academic" className="py-20" style={{ background: "#f0f4f8" }}>
      <div className="container mx-auto px-4">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="text-center mb-14">
          <motion.span variants={fadeUp} className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 mb-3">
            ✓ Currently Available · Operational
          </motion.span>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-primary mb-4">Academic Assistance Services</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Comprehensive academic support for healthcare students and postgraduate scholars. From research proposals and data analysis to literature reviews, thesis writing, and article publication — we cover every stage of the academic journey with a 72-hour turnaround and Harvard referencing standards.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Services list */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer}>
            <motion.h3 variants={fadeUp} className="text-lg font-bold text-foreground mb-5">Our Services</motion.h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services.map(({ icon: Icon, label }) => (
                <motion.div key={label} variants={fadeUp} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-border shadow-sm">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#e8eff7" }}>
                    <Icon size={16} style={{ color: "#1A67B1" }} />
                  </div>
                  <span className="text-sm font-medium text-foreground leading-snug">{label}</span>
                </motion.div>
              ))}
            </div>
            <motion.div variants={fadeUp} className="mt-6">
              <button onClick={scrollToContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90"
                style={{ background: "#0A427F" }}>
                Contact Us <ChevronRight size={16} />
              </button>
            </motion.div>
          </motion.div>

          {/* WHO WE ASSIST */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer}>
            <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden border-2" style={{ borderColor: "#1A67B1" }}>
              {/* Header */}
              <div className="px-7 py-5" style={{ background: "#0A427F" }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                    <Users size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">WHO WE ASSIST</h3>
                    <p className="text-xs text-white/70">For manuscript &amp; article writing specifically</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="bg-white px-7 py-6 space-y-4">
                <motion.div variants={fadeUp} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#e8eff7" }}>
                    <GraduationCap size={18} style={{ color: "#0A427F" }} />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm">Masters &amp; PhD Completers Struggling to Publish</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Postgraduate scholars who have completed their degrees but need support transforming their research into publishable articles.
                    </p>
                  </div>
                </motion.div>

                <div className="border-t border-border" />

                <motion.div variants={fadeUp} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#e8eff7" }}>
                    <FlaskConical size={18} style={{ color: "#0A427F" }} />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm">Qualitative Research Focus</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Researchers whose studies are rooted in qualitative methodologies — thematic analysis, grounded theory, and phenomenology.
                    </p>
                  </div>
                </motion.div>

                <div className="border-t border-border" />

                <motion.div variants={fadeUp} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#e8eff7" }}>
                    <BookMarked size={18} style={{ color: "#0A427F" }} />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm">Clients with a Preselected Journal</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Scholars who have already identified a DHET-accredited journal aligned to their research topic and scope.
                    </p>
                  </div>
                </motion.div>

                <div className="border-t border-border" />

                <motion.div variants={fadeUp} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-red-50">
                    <Ban size={18} className="text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-600 text-sm">No Scoping Reviews</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      We do not provide scoping reviews. Our focus is on primary qualitative research manuscript writing and article development.
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Footer */}
              <div className="px-7 py-4 bg-muted/40 border-t border-border">
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <Star size={12} style={{ color: "#1A67B1" }} />
                  The criteria above apply specifically to our manuscript &amp; article writing service for DHET-accredited journals.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Business Areas ───────────────────────────────────────────────────────────
function BusinessAreas() {
  const areas = [
    {
      id: "rental", icon: Building2, title: "Rental Property Business",
      desc: "Professional property management and rental services ensuring optimal returns for property owners while providing quality accommodation for tenants.",
      tags: ["Property Management", "Tenant Screening", "Maintenance Coordination", "Rental Collection"],
    },
    {
      id: "airbnb", icon: Home, title: "Air BnB Business",
      desc: "Vacation rental management services offering short-term accommodation solutions. We handle listing optimization, guest communication, and property maintenance.",
      tags: ["Listing Optimization", "Guest Communication", "Property Maintenance", "Pricing Strategy"],
    },
    {
      id: "farming", icon: Tractor, title: "Farming and Livestock",
      desc: "Agricultural operations focusing on sustainable farming practices and livestock management, combining traditional methods with modern agricultural technology.",
      tags: ["Crop Production", "Livestock Management", "Sustainable Practices", "Agricultural Consulting"],
    },
  ];
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="text-center mb-14">
          <motion.p variants={fadeUp} className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Expanding Portfolio</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-primary mb-4">Our Business Areas</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            We are building a diverse portfolio of services to serve our community. Currently operational in academic assistance, with exciting ventures launching soon.
          </motion.p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6">
          {areas.map(({ id, icon: Icon, title, desc, tags }) => (
            <motion.div key={id} id={id} variants={fadeUp} className="rounded-2xl border border-border bg-muted/20 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#e8eff7" }}>
                  <Icon size={22} style={{ color: "#0A427F" }} />
                </div>
                <span className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-600 font-semibold border border-amber-200">Under Construction</span>
              </div>
              <h3 className="font-bold text-foreground mb-3">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">{desc}</p>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground font-medium">{t}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Contact ──────────────────────────────────────────────────────────────────
function Contact() {
  return (
    <section id="contact" className="py-20" style={{ background: "#f0f4f8" }}>
      <div className="container mx-auto px-4">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Reach Out</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-primary mb-4">Get in Touch</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto">
            Ready to start your academic journey or inquire about our upcoming services? Contact us today.
          </motion.p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {[
            { icon: Mail, label: "Email", value: "info@hlopheog.com" },
            { icon: MapPin, label: "Location", value: "KwaZulu Natal, South Africa" },
            { icon: Phone, label: "Registration", value: "CIPC: 2025/912423/07" },
          ].map(({ icon: Icon, label, value }) => (
            <motion.div key={label} variants={fadeUp} className="bg-white rounded-xl p-6 text-center border border-border shadow-sm">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "#e8eff7" }}>
                <Icon size={22} style={{ color: "#0A427F" }} />
              </div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1">{label}</p>
              <p className="text-sm font-medium text-foreground">{value}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── Payment Information ──────────────────────────────────────────────────────
function PaymentInformation() {
  return (
    <section id="payment" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="text-center mb-12">
          <motion.p variants={fadeUp} className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">Payments</motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-primary mb-4">Payment Information</motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto">
            Transparent pricing and flexible payment options.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer}
          className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">

          {/* Banking Details Card */}
          <motion.div variants={fadeUp} className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: "#1A67B1" }}>
            <div className="px-6 py-4" style={{ background: "#0A427F" }}>
              <h3 className="text-white font-bold text-base">Banking Details</h3>
              <p className="text-white/60 text-xs mt-0.5">Direct EFT / Bank Transfer</p>
            </div>
            <div className="bg-white px-6 py-5 space-y-3">
              {[
                { label: "Bank", value: "Capitec Bank" },
                { label: "Account Name", value: "Hlophe Ongemhlophe Group" },
                { label: "Account Number", value: "1054 6537 12" },
                { label: "Branch Code", value: "CABLZAJJ" },
                { label: "Account Type", value: "Business Account" },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
                  <span className="text-sm font-semibold text-foreground font-mono">{value}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Payment Terms Card */}
          <motion.div variants={fadeUp} className="rounded-2xl border border-border bg-muted/20 overflow-hidden">
            <div className="px-6 py-4" style={{ background: "#f0f4f8" }}>
              <h3 className="font-bold text-base text-primary">Payment Terms</h3>
              <p className="text-muted-foreground text-xs mt-0.5">How it works</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              {[
                { step: "01", title: "100% Deposit Required", desc: "Full payment is required upfront upon project acceptance before work commences." },
                { step: "02", title: "Proof of Payment", desc: "Send your proof of payment to info@hlopheog.com to confirm your booking." },
                { step: "03", title: "72-Hour Turnaround", desc: "Work begins immediately upon receipt of full payment and all required materials." },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-bold text-xs text-white" style={{ background: "#1A67B1" }}>
                    {step}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-3 bg-muted/40 border-t border-border">
              <p className="text-xs text-muted-foreground">
                💡 <span className="font-medium">Reference:</span> Use your name + service type as payment reference (e.g. "Sipho – Research Proposal").
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Additional Services */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer}
          className="max-w-3xl mx-auto mt-6">
          <motion.div variants={fadeUp} className="rounded-2xl border border-border bg-muted/20 overflow-hidden">
            <div className="px-6 py-4" style={{ background: "#f0f4f8" }}>
              <h3 className="font-bold text-base text-primary">Additional Services</h3>
            </div>
            <div className="px-6 py-5 grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#e8eff7" }}>
                  <Clock size={16} style={{ color: "#1A67B1" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Rush Fee Available</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Expedited turnaround available on request. An additional rush fee will apply based on urgency and scope of work.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#e8eff7" }}>
                  <CheckCircle size={16} style={{ color: "#1A67B1" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Revisions Available</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Revisions are accessible to all clients. Additional charges may apply depending on the scope of changes requested.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <footer style={{ background: "#0A427F" }} className="text-white">
      <div className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <h3 className="font-bold text-lg mb-1">Hlophe Ongemhlophe Group</h3>
            <p className="text-white/50 text-xs font-mono tracking-widest mb-4">PTY LTD</p>
            <p className="text-white/70 text-sm leading-relaxed">Building excellence across multiple industries in South Africa.</p>
          </div>
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-white/60 mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { label: "Academic Assistance", href: "#academic" },
                { label: "About Us", href: "#about" },
                { label: "Contact", href: "#contact" },
              ].map(({ label, href }) => (
                <li key={href}>
                  <button onClick={() => scrollTo(href)} className="text-sm text-white/70 hover:text-white transition-colors">{label}</button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-white/60 mb-4">Contact Information</h4>
            <div className="space-y-2 text-sm text-white/70">
              <p>info@hlopheog.com</p>
              <p>hlopheog.com</p>
              <p>KwaZulu Natal, South Africa</p>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-white/60 mb-4">Registration Details</h4>
            <div className="space-y-1 text-sm text-white/70 font-mono">
              <p>CIPC: <span className="text-white">2025/912423/07</span></p>
              <p>B-BBEE: <span className="text-white">9448473376</span></p>
            </div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-white/60 mt-5 mb-3">Banking Details</h4>
            <div className="space-y-1 text-sm text-white/70 font-mono">
              <p>Bank: <span className="text-white">Capitec Bank</span></p>
              <p>Acc: <span className="text-white">1054 6537 12</span></p>
              <p>Branch: <span className="text-white">CABLZAJJ</span></p>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 mt-10 pt-6 text-center text-xs text-white/40">
          © 2026 Hlophe Ongemhlophe Group (PTY) LTD. All rights reserved. | Registered in South Africa | Tax Compliant
        </div>
      </div>
    </footer>
  );
}

// ─── Disclaimer ───────────────────────────────────────────────────────────────
function Disclaimer() {
  return (
    <section className="w-full py-14 bg-white border-y border-border">
      <div className="container mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeUp} className="flex items-start gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#e8eff7" }}>
              <Shield size={20} style={{ color: "#0A427F" }} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-accent mb-1">
                Important Notice — Please Read Before Engaging Our Services
              </p>
              <h2 className="text-xl md:text-2xl font-bold text-primary leading-snug">
                Technology-Assisted Academic Services Disclaimer
              </h2>
            </div>
          </motion.div>

          {/* Body */}
          <motion.div variants={fadeUp} className="space-y-5 text-sm text-muted-foreground leading-relaxed max-w-5xl">

            <p>
              <span className="font-semibold text-foreground">Use of Assistive Technology &amp; Artificial Intelligence: </span>
              Hlophe Ongemhlophe Group (PTY) LTD hereby discloses that its academic assistance services are produced through the
              deliberate and informed application of assistive devices, artificial intelligence (AI) tools, and specialised
              academic software. These technologies are employed as instruments of knowledge generation, information synthesis,
              and academic writing support, and form an integral part of our service delivery methodology.
            </p>

            <p>
              <span className="font-semibold text-foreground">Integration of Professional Knowledge &amp; Experience: </span>
              The use of technology does not operate in isolation. All work produced under this business is further guided,
              reviewed, and completed through the professional knowledge, academic training, and lived experience of our
              consultants — who hold postgraduate qualifications in healthcare and related disciplines. The final deliverable
              is the product of both technological capability and human intellectual rigour.
            </p>

            <p>
              <span className="font-semibold text-foreground">Why This Combination is Important, Efficient &amp; Effective: </span>
              The integration of artificial intelligence with qualified human expertise represents the contemporary standard
              of knowledge work. AI enables rapid information processing, structural coherence, and broad literature
              engagement, while human oversight ensures contextual accuracy, disciplinary relevance, ethical alignment,
              and compliance with academic standards such as Harvard referencing. Together, these elements produce work
              that is not only timely — within 72 hours — but also substantive, credible, and tailored to the client's
              specific research context. This hybrid approach is demonstrably more efficient than either human effort or
              AI alone, and reflects best practice in modern academic and professional consulting.
            </p>

            {/* Acceptance box */}
            <div className="rounded-xl px-5 py-4 border border-green-200 bg-green-50">
              <p className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <CheckCircle size={15} className="text-green-600 shrink-0" />
                Acceptance of Terms by Proceeding to Payment
              </p>
              <p className="text-green-900/80">
                Any client who proceeds to make payment for any service offered by Hlophe Ongemhlophe Group (PTY) LTD is
                deemed to have read, understood, and unconditionally accepted this disclaimer in its entirety. Payment
                constitutes informed consent to the use of artificial intelligence, assistive technology, and software
                as described herein, in combination with human professional expertise, in the fulfilment of the requested
                academic assistance service.
              </p>
            </div>

            {/* Advisory box */}
            <div className="rounded-xl px-5 py-4 border border-amber-200 bg-amber-50">
              <p className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <Ban size={15} className="text-amber-600 shrink-0" />
                Advisory to Clients with Institutional Restrictions
              </p>
              <p className="text-amber-900/80">
                Clients whose academic institutions, professional bodies, or regulatory frameworks expressly prohibit
                the use of artificial intelligence or AI-assisted tools in the production of academic work are strongly
                advised <span className="font-semibold text-amber-900">not</span> to engage the services of this business.
                It is the sole responsibility of the client to ascertain and comply with the policies of their respective
                institution or regulatory body prior to commissioning any work. Hlophe Ongemhlophe Group (PTY) LTD
                accepts no liability arising from a client's failure to conduct such due diligence.
              </p>
            </div>

          </motion.div>

          <motion.p variants={fadeUp} className="mt-6 text-xs text-muted-foreground font-mono">
            © 2026 Hlophe Ongemhlophe Group (PTY) LTD · CIPC: 2025/912423/07 · This disclaimer forms part of the standard terms of engagement for all services rendered by this business.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
const Index = () => (
  <div className="min-h-screen font-sans">
    <Navbar />
    <Hero />
    <About />
    <AcademicAssistance />
    <Disclaimer />
    <BusinessAreas />
    <Contact />
    <PaymentInformation />
    <Footer />
  </div>
);

export default Index;
